import { useState, useEffect } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { 
  ArrowLeft, 
  Star, 
  Upload, 
  X, 
  MapPin, 
  Building2,
  Calendar,
  DollarSign,
  Camera,
  CheckCircle,
  ImageIcon,
  PenTool
} from "lucide-react";
import Header from "@/components/header";
import Footer from "@/components/Footer1";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

// Mock campaign data - in real app this would come from the API
const mockCampaignData = {
  id: 1,
  name: "Summer Fashion Campaign",
  duration: "01 Jun 2024 - 30 Jun 2024",
  status: "Completed",
  totalBudget: 15000,
  inventories: [
    {
      id: 1,
      name: "Times Square Digital Premier",
      mediaOwner: "OOH Unmissable", 
      location: "New York, NY",
      type: "Digital",
      price: 5000
    },
    {
      id: 2,
      name: "Sydney CBD Premium",
      mediaOwner: "QMS Media Limited",
      location: "Sydney, NSW",
      type: "Digital", 
      price: 7000
    },
    {
      id: 3,
      name: "Melbourne Central Plaza",
      mediaOwner: "AdSpace Networks",
      location: "Melbourne, VIC",
      type: "Classic",
      price: 3000
    }
  ]
};

// Star rating component
function StarRating({ rating, onRatingChange, readonly = false }: { 
  rating: number; 
  onRatingChange?: (rating: number) => void;
  readonly?: boolean;
}) {
  return (
    <div className="flex items-center space-x-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={readonly}
          onClick={() => !readonly && onRatingChange?.(star)}
          className={`${readonly ? 'cursor-default' : 'cursor-pointer hover:scale-110'} transition-transform`}
        >
          <Star
            className={`h-5 w-5 ${
              star <= rating 
                ? 'fill-yellow-400 text-yellow-400' 
                : 'text-gray-300'
            }`}
          />
        </button>
      ))}
    </div>
  );
}

export default function CampaignReview() {
  const location = useLocation();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Get campaign ID from URL params
  const urlParams = new URLSearchParams(location.search);
  const campaignId = urlParams.get('campaign');

  // Form state
  const [selectedMediaOwner, setSelectedMediaOwner] = useState("");
  const [reviewTitle, setReviewTitle] = useState("");
  const [reviewText, setReviewText] = useState("");
  const [overallRating, setOverallRating] = useState(0);
  const [serviceQuality, setServiceQuality] = useState(0);
  const [adPlacement, setAdPlacement] = useState(0);
  const [communication, setCommunication] = useState(0);
  const [wouldRecommend, setWouldRecommend] = useState(false);
  const [uploadedImages, setUploadedImages] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submittedReviews, setSubmittedReviews] = useState<any[]>([]);
  const [editingReview, setEditingReview] = useState<any>(null);

  // Fetch existing reviews for this campaign
  const { data: existingReviews } = useQuery({
    queryKey: [`/api/campaign-reviews/campaign/${campaignId}`],
    enabled: !!campaignId,
  });

  // Update submitted reviews when data is fetched
  useEffect(() => {
    if (existingReviews) {
      setSubmittedReviews(Array.isArray(existingReviews) ? existingReviews : []);
    }
  }, [existingReviews]);

  // Get unique media owners from campaign inventories
  const mediaOwners = [...new Set(mockCampaignData.inventories.map(inv => inv.mediaOwner))];

  const submitReview = useMutation({
    mutationFn: async (reviewData: any) => {
      if (editingReview) {
        return apiRequest("PUT", `/api/campaign-reviews/${editingReview.id}`, reviewData);
      } else {
        return apiRequest("POST", "/api/campaign-reviews", reviewData);
      }
    },
    onSuccess: (response) => {
      toast({
        title: editingReview ? "Review Updated!" : "Review Submitted!",
        description: editingReview ? "Your review has been updated successfully." : "Thank you for your feedback. Your review has been submitted successfully.",
      });
      
      // Invalidate and refetch the campaign reviews
      queryClient.invalidateQueries({ 
        queryKey: [`/api/campaign-reviews/campaign/${campaignId}`] 
      });
      
      // Add/update the review in submitted reviews
      if (editingReview) {
        setSubmittedReviews(prev => 
          prev.map(review => review.id === editingReview.id ? response : review)
        );
      } else {
        setSubmittedReviews(prev => [...prev, response]);
      }
      
      // Reset form
      resetForm();
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: editingReview ? "Failed to update review. Please try again." : "Failed to submit review. Please try again.",
        variant: "destructive",
      });
    },
  });

  const resetForm = () => {
    setSelectedMediaOwner("");
    setReviewTitle("");
    setReviewText("");
    setOverallRating(0);
    setServiceQuality(0);
    setAdPlacement(0);
    setCommunication(0);
    setWouldRecommend(false);
    setUploadedImages([]);
    setEditingReview(null);
  };

  const handleEditReview = (review: any) => {
    setEditingReview(review);
    setSelectedMediaOwner(review.mediaOwner);
    setReviewTitle(review.reviewTitle);
    setReviewText(review.reviewText);
    setOverallRating(review.rating);
    setServiceQuality(review.serviceQuality || 0);
    setAdPlacement(review.adPlacement || 0);
    setCommunication(review.communication || 0);
    setWouldRecommend(review.wouldRecommend);
    setUploadedImages(review.reviewImages || []);
  };

  const handleSubmitReview = async () => {
    if (!selectedMediaOwner || !reviewTitle.trim() || !reviewText.trim() || overallRating === 0) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields and provide a rating.",
        variant: "destructive"
      });
      return;
    }

    const reviewData = {
      campaignId: parseInt(campaignId || "1"),
      mediaOwner: selectedMediaOwner,
      rating: overallRating,
      reviewTitle: reviewTitle.trim(),
      reviewText: reviewText.trim(),
      reviewImages: uploadedImages,
      serviceQuality,
      adPlacement,
      communication,
      wouldRecommend
    };

    submitReview.mutate(reviewData);
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      Array.from(files).forEach((file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          if (e.target?.result) {
            setUploadedImages(prev => [...prev, e.target!.result as string]);
          }
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const removeImage = (index: number) => {
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate("/my-campaigns")}
            className="mb-4 text-emerald-600 hover:text-emerald-700"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Campaigns
          </Button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-emerald-600">Campaign Review</h1>
              <p className="text-gray-600 mt-2">Share your experience with media owners</p>
            </div>
            <div className="text-right">
              <Badge variant="outline" className="mb-2">
                <CheckCircle className="h-3 w-3 mr-1" />
                Completed
              </Badge>
              <p className="text-sm text-gray-600">{mockCampaignData.name}</p>
            </div>
          </div>
        </div>

        {/* Campaign Summary */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="text-emerald-600">Campaign Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="flex items-center">
                <Calendar className="h-5 w-5 text-emerald-600 mr-3" />
                <div>
                  <p className="text-sm text-gray-600">Duration</p>
                  <p className="font-medium">{mockCampaignData.duration}</p>
                </div>
              </div>
              
              <div className="flex items-center">
                <DollarSign className="h-5 w-5 text-emerald-600 mr-3" />
                <div>
                  <p className="text-sm text-gray-600">Total Budget</p>
                  <p className="font-medium">${mockCampaignData.totalBudget?.toLocaleString()}</p>
                </div>
              </div>
              
              <div className="flex items-center">
                <Building2 className="h-5 w-5 text-emerald-600 mr-3" />
                <div>
                  <p className="text-sm text-gray-600">Media Owners</p>
                  <p className="font-medium">{mediaOwners.length} Partners</p>
                </div>
              </div>
            </div>

            <Separator className="my-6" />

            <div>
              <h4 className="font-medium text-gray-900 mb-4">Billboard Locations</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {mockCampaignData.inventories.map((inventory) => (
                  <div key={inventory.id} className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h5 className="font-medium text-sm">{inventory.name}</h5>
                      <Badge variant="outline" className="text-xs">
                        {inventory.type}
                      </Badge>
                    </div>
                    <p className="text-xs text-gray-600 mb-1">
                      <MapPin className="h-3 w-3 inline mr-1" />
                      {inventory.location}
                    </p>
                    <p className="text-xs text-emerald-600 font-medium">{inventory.mediaOwner}</p>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Submitted Reviews */}
        {submittedReviews.length > 0 && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="text-emerald-600">Your Reviews</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {submittedReviews.map((review) => (
                  <div key={review.id} className="border rounded-lg p-4 bg-gray-50">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h4 className="font-medium text-emerald-600">{review.mediaOwner}</h4>
                        <div className="flex items-center mt-1">
                          <StarRating rating={review.rating} readonly />
                          <span className="ml-2 text-sm text-gray-600">{review.rating} star{review.rating !== 1 ? 's' : ''}</span>
                        </div>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditReview(review)}
                        className="text-emerald-600 hover:text-emerald-700"
                      >
                        Edit Review
                      </Button>
                    </div>
                    
                    <h5 className="font-medium mb-2">{review.reviewTitle}</h5>
                    <p className="text-gray-700 mb-3">{review.reviewText}</p>
                    
                    {review.reviewImages && review.reviewImages.length > 0 && (
                      <div className="flex space-x-2 mb-3">
                        {review.reviewImages.map((image: string, index: number) => (
                          <img
                            key={index}
                            src={image}
                            alt={`Review image ${index + 1}`}
                            className="w-16 h-16 object-cover rounded"
                          />
                        ))}
                      </div>
                    )}
                    
                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                      {review.serviceQuality && (
                        <span>Service: {review.serviceQuality}⭐</span>
                      )}
                      {review.adPlacement && (
                        <span>Placement: {review.adPlacement}⭐</span>
                      )}
                      {review.communication && (
                        <span>Communication: {review.communication}⭐</span>
                      )}
                      {review.wouldRecommend && (
                        <Badge variant="outline" className="text-emerald-600">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Recommended
                        </Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Review Form */}
        <Card>
          <CardHeader>
            <CardTitle className="text-emerald-600 flex items-center">
              <PenTool className="h-5 w-5 mr-2" />
              {editingReview ? "Edit Review" : "Write Review"}
            </CardTitle>
            {editingReview && (
              <div className="flex items-center justify-between">
                <p className="text-sm text-gray-600">Editing review for {editingReview.mediaOwner}</p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={resetForm}
                  className="text-gray-600 hover:text-gray-700"
                >
                  Cancel Edit
                </Button>
              </div>
            )}
          </CardHeader>
          <CardContent className="space-y-6">
            
            {/* Media Owner Selection */}
            <div>
              <Label htmlFor="mediaOwner">Select Media Owner to Review *</Label>
              <Select value={selectedMediaOwner} onValueChange={setSelectedMediaOwner} disabled={!!editingReview}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Choose a media owner..." />
                </SelectTrigger>
                <SelectContent>
                  {mediaOwners.filter(owner => 
                    !submittedReviews.some(review => review.mediaOwner === owner) || 
                    (editingReview && editingReview.mediaOwner === owner)
                  ).map((owner) => (
                    <SelectItem key={owner} value={owner}>
                      {owner}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Overall Rating */}
            <div>
              <Label>Overall Rating *</Label>
              <div className="flex items-center space-x-3 mt-2">
                <StarRating rating={overallRating} onRatingChange={setOverallRating} />
                <span className="text-sm text-gray-600">
                  {overallRating > 0 && `${overallRating} star${overallRating !== 1 ? 's' : ''}`}
                </span>
              </div>
            </div>

            {/* Review Title */}
            <div>
              <Label htmlFor="reviewTitle">Review Title *</Label>
              <Input
                id="reviewTitle"
                value={reviewTitle}
                onChange={(e) => setReviewTitle(e.target.value)}
                placeholder="Summarize your experience..."
                className="mt-1"
              />
            </div>

            {/* Review Text */}
            <div>
              <Label htmlFor="reviewText">Review Details *</Label>
              <Textarea
                id="reviewText"
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
                placeholder="Share your detailed experience with this media owner..."
                rows={5}
                className="mt-1"
              />
            </div>

            {/* Detailed Ratings */}
            <div>
              <Label className="text-base font-medium">Detailed Ratings</Label>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-3">
                
                <div>
                  <Label className="text-sm">Service Quality</Label>
                  <StarRating rating={serviceQuality} onRatingChange={setServiceQuality} />
                </div>
                
                <div>
                  <Label className="text-sm">Ad Placement</Label>
                  <StarRating rating={adPlacement} onRatingChange={setAdPlacement} />
                </div>
                
                <div>
                  <Label className="text-sm">Communication</Label>
                  <StarRating rating={communication} onRatingChange={setCommunication} />
                </div>
              </div>
            </div>

            {/* Would Recommend */}
            <div className="flex items-center space-x-3">
              <Switch
                id="recommend"
                checked={wouldRecommend}
                onCheckedChange={setWouldRecommend}
              />
              <Label htmlFor="recommend">Would you recommend this media owner?</Label>
            </div>

            {/* Image Upload */}
            <div>
              <Label>Upload Photos (Optional)</Label>
              <p className="text-sm text-gray-600 mb-3">Share photos from your campaign experience</p>
              
              <div className="space-y-4">
                {/* Upload Button */}
                <div>
                  <input
                    type="file"
                    id="image-upload"
                    multiple
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => document.getElementById('image-upload')?.click()}
                    className="border-dashed border-2 w-full py-8"
                  >
                    <Upload className="h-5 w-5 mr-2" />
                    Click to upload images
                  </Button>
                </div>

                {/* Uploaded Images */}
                {uploadedImages.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {uploadedImages.map((image, index) => (
                      <div key={index} className="relative group">
                        <div className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                          <img
                            src={image}
                            alt={`Upload ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => removeImage(index)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-6">
              <Button
                onClick={handleSubmitReview}
                disabled={isSubmitting || !selectedMediaOwner || !reviewTitle.trim() || !reviewText.trim() || overallRating === 0}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                {isSubmitting ? (editingReview ? "Updating Review..." : "Submitting Review...") : (editingReview ? "Update Review" : "Submit Review")}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
      
      <Footer configData={[]} yourLogo={""} />
    </div>
  );
};